// Configuration des durées disponibles selon le montant emprunté
// Basé sur les grilles tarifaires Cofidis

export interface DurationOption {
  value: string;
  label: string;
  months: number;
}

export const ALL_DURATIONS: DurationOption[] = [
  { value: '12', label: '12 mois', months: 12 },
  { value: '24', label: '24 mois', months: 24 },
  { value: '36', label: '36 mois', months: 36 },
  { value: '48', label: '48 mois', months: 48 },
  { value: '60', label: '60 mois', months: 60 },
  { value: '72', label: '72 mois', months: 72 },
  { value: '84', label: '84 mois', months: 84 }
];

/**
 * Retourne les durées disponibles en fonction du montant emprunté
 * Basé sur la grille tarifaire Cofidis officielle
 * @param amount - Montant en euros
 * @returns Liste des durées disponibles
 */
export function getAvailableDurations(amount: number): DurationOption[] {
  // 500€ - 3000€ : 12 à 60 mois
  if (amount >= 500 && amount <= 3000) {
    return ALL_DURATIONS.filter(d => d.months <= 60);
  }

  // 3500€ - 7500€ : 12 à 72 mois
  if (amount >= 3500 && amount <= 7500) {
    return ALL_DURATIONS.filter(d => d.months <= 72);
  }

  // 8000€ - 35000€ : 12 à 84 mois
  if (amount >= 8000 && amount <= 35000) {
    return ALL_DURATIONS;
  }

  // Par défaut (montant invalide), retourner toutes les durées
  return ALL_DURATIONS;
}

/**
 * Obtient le TNA (Taux Nominal Annuel) en fonction du montant et de la durée
 * @param amount - Montant en euros
 * @param duration - Durée en mois
 * @returns TNA en pourcentage (ex: 7.97)
 */
export function getTNA(amount: number, duration: number): number {
  // 500€ - 3000€ : TNA = 20.62%
  if (amount >= 500 && amount <= 3000) {
    return 20.62;
  }

  // 3500€ - 6000€ : TNA = 13.68%
  if (amount >= 3500 && amount <= 6000) {
    return 13.68;
  }

  // 6500€ - 35000€ avec durée 12 mois : TNA = 0.90%
  if (amount >= 6500 && amount <= 35000 && duration === 12) {
    return 0.90;
  }

  // 11000€ - 11500€ avec durée 72 mois : TNA = 7.47%
  if (amount >= 11000 && amount <= 11500 && duration === 72) {
    return 7.47;
  }

  // 6500€ - 35000€ (sauf cas particuliers ci-dessus) : TNA = 7.97%
  if (amount >= 6500 && amount <= 35000) {
    return 7.97;
  }

  // Défaut
  return 7.97;
}

/**
 * Calcule la mensualité approximative
 * @param amount - Montant emprunté
 * @param duration - Durée en mois
 * @returns Mensualité estimée
 */
export function calculateMonthlyPayment(amount: number, duration: number): number {
  if (duration === 0) return 0;

  const tna = getTNA(amount, duration);
  const monthlyRate = tna / 100 / 12;

  if (monthlyRate === 0) {
    return amount / duration;
  }

  const payment = (amount * monthlyRate * Math.pow(1 + monthlyRate, duration)) /
                  (Math.pow(1 + monthlyRate, duration) - 1);

  return Math.round(payment * 100) / 100;
}
